import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Users, TrendingUp, Search, FolderOpen, Loader2, FileEdit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getWorkspaceProjects, getUserDraftsInWorkspace } from '@/lib/database';
import { useWorkspace } from '@/store/workspace';
import { useAuthStore } from '@/store/auth';
import { useProjectsRefresh } from '@/store/projectsRefresh';
import type { Database, Tables } from '@/types/database.types';
import { format } from 'date-fns';

type DraftRow = Tables<'projects'>;
type ProjectStatus = Database['public']['Enums']['project_status'];

interface ProjectWithStats {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  overall_health: 'green' | 'yellow' | 'red';
  created_by: string;
  created_at: string;
  updated_at: string;
  settings: any;
  progress: number;
  nextMilestone: string | null;
  nextMilestoneDue: string | null;
  totalMilestones: number;
  completedMilestones: number;
  teamMembers: number;
}

function showDraftsKey(userId: string, workspaceId: string): string {
  return `ingesta-show-drafts:${userId}:${workspaceId}`;
}

function readShowDrafts(userId: string | null, workspaceId: string | null): boolean {
  if (!userId || !workspaceId) return false;
  try {
    return localStorage.getItem(showDraftsKey(userId, workspaceId)) === '1';
  } catch {
    return false;
  }
}

function writeShowDrafts(userId: string, workspaceId: string, value: boolean): void {
  try {
    localStorage.setItem(showDraftsKey(userId, workspaceId), value ? '1' : '0');
  } catch {
    /* localStorage unavailable — toggle reverts to default on next load */
  }
}

export function ProjectList() {
  const navigate = useNavigate();
  const { currentWorkspace, canEdit } = useWorkspace();
  const userId = useAuthStore(s => s.user?.id ?? null);
  const projectsVersion = useProjectsRefresh(s => s.version);
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDrafts, setShowDrafts] = useState(false);

  // Read the user's "Show drafts" preference once both userId and workspace
  // are available. Without both, the localStorage key isn't valid yet.
  useEffect(() => {
    if (!userId || !currentWorkspace) return;
    setShowDrafts(readShowDrafts(userId, currentWorkspace.id));
  }, [userId, currentWorkspace?.id]);

  // Reload when workspace changes OR when a project mutation elsewhere
  // (rename, promote, delete) bumps the version.
  useEffect(() => {
    loadProjects();
  }, [currentWorkspace, projectsVersion]);

  const loadProjects = async () => {
    if (!currentWorkspace) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [activeData, draftData] = await Promise.all([
        getWorkspaceProjects(currentWorkspace.id),
        getUserDraftsInWorkspace(currentWorkspace.id),
      ]);
      setProjects(activeData as ProjectWithStats[]);
      setDrafts(draftData);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDrafts = (next: boolean) => {
    setShowDrafts(next);
    if (userId && currentWorkspace) {
      writeShowDrafts(userId, currentWorkspace.id, next);
    }
  };

  // Filter projects based on search query, then optionally splice in drafts.
  const queryMatches = (name: string, description: string | null) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return name.toLowerCase().includes(q) || (description?.toLowerCase().includes(q) ?? false);
  };

  const filteredActive = projects.filter(p => queryMatches(p.name, p.description));

  const filteredDrafts: ProjectWithStats[] = showDrafts
    ? drafts
        .filter(d => queryMatches(d.name, d.description))
        .map<ProjectWithStats>(d => ({
          id: d.id,
          workspace_id: d.workspace_id,
          name: d.name,
          description: d.description,
          status: d.status,
          overall_health: (d.overall_health ?? 'green') as 'green' | 'yellow' | 'red',
          created_by: d.created_by,
          created_at: d.created_at ?? '',
          updated_at: d.updated_at ?? d.created_at ?? '',
          settings: d.settings,
          progress: 0,
          nextMilestone: null,
          nextMilestoneDue: null,
          totalMilestones: 0,
          completedMilestones: 0,
          teamMembers: 0,
        }))
    : [];

  // Drafts first (they're the user's most recent/in-progress work), then active.
  const displayProjects = [...filteredDrafts, ...filteredActive];

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'green': return 'text-green-600 bg-green-50 border-green-200';
      case 'yellow': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'red': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getHealthLabel = (health: string) => {
    switch (health) {
      case 'green': return 'On Track';
      case 'yellow': return 'At Risk';
      case 'red': return 'Off Track';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No workspace selected
        </h3>
        <p className="text-gray-600">
          Please select or create a workspace first
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-1">
            Manage and track all your team projects
          </p>
        </div>
        {canEdit() && (
          <Button
            onClick={() => navigate('/projects/create')}
            className="mt-4 sm:mt-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search projects..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 items-center">
          {drafts.length > 0 && (
            <Button
              variant={showDrafts ? 'default' : 'outline'}
              onClick={() => handleToggleDrafts(!showDrafts)}
              size="default"
            >
              <FileEdit className="h-4 w-4 mr-1" />
              {showDrafts ? 'Hide drafts' : `Show drafts (${drafts.length})`}
            </Button>
          )}
          <Button variant="outline">All Status</Button>
          <Button variant="outline">This Month</Button>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {displayProjects.map((project) => {
          const isDraft = project.status === 'draft';
          return (
            <Card
              key={project.id}
              className="relative cursor-pointer hover:shadow-lg transition-shadow overflow-hidden h-full flex flex-col"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              {isDraft && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                >
                  <span className="select-none -rotate-12 text-6xl font-extrabold tracking-widest text-gray-300/60">
                    DRAFT
                  </span>
                </div>
              )}
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate mb-1">
                      {project.name}
                    </CardTitle>
                    {project.description && (
                      <CardDescription className="line-clamp-4">
                        {project.description}
                      </CardDescription>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getHealthColor(project.overall_health)}`}>
                    {getHealthLabel(project.overall_health)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col gap-4">
                {/* Drafts: minimal body — no progress bar / milestones / team count */}
                {isDraft ? (
                  <div className="mt-auto text-xs text-gray-500 pt-2 border-t">
                    Started: {project.created_at ? format(new Date(project.created_at), 'MMM dd, yyyy') : '—'}
                  </div>
                ) : (
                  <>
                    {/* Progress */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-ingesta-teal-500 h-2 rounded-full transition-all"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Next Milestone */}
                    {project.nextMilestone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <TrendingUp className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">Next:</span>
                        <span className="font-medium">{project.nextMilestone}</span>
                      </div>
                    )}

                    {/* Team and Due Date */}
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>{project.teamMembers} members</span>
                      </div>
                      {project.nextMilestoneDue && (
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>Due {format(new Date(project.nextMilestoneDue), 'MMM dd')}</span>
                        </div>
                      )}
                    </div>

                    {/* Last Update — pinned to the bottom regardless of how
                        much body content sits above it, so cards in a row
                        stay vertically aligned. */}
                    <div className="mt-auto text-xs text-gray-500 pt-2 border-t">
                      Last updated: {project.updated_at ? format(new Date(project.updated_at), 'MMM dd, yyyy') : '—'}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {displayProjects.length === 0 && !loading && (
        <div className="col-span-full">
          <Card className="border-2 border-dashed border-gray-300 bg-gray-50/50">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-gray-100 p-3 mb-4">
                <FolderOpen className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchQuery ? 'No projects found' : 'No projects yet'}
              </h3>
              <p className="text-gray-600 mb-6 max-w-sm text-center">
                {searchQuery
                  ? `No projects match "${searchQuery}". Try adjusting your search.`
                  : 'Projects help you organize your work and track progress. Create your first project to get started.'}
              </p>
              {!searchQuery && canEdit() && (
                <Button
                  onClick={() => navigate('/projects/create')}
                  size="lg"
                  className="gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Create Your First Project
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
